import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Fab,
  Avatar,
  Stack,
  CircularProgress,
  useMediaQuery,
  Dialog,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CallIcon from "@mui/icons-material/Call";
import AddAPhotoIcon from "@mui/icons-material/AddAPhoto";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import HealthAndSafetyOutlinedIcon from "@mui/icons-material/HealthAndSafetyOutlined";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { ENDPOINT } from "../configuration/configuration";
import { useSelector } from "react-redux";
import LogoBetweenLines from "../components/LogoBetweenLines";
import SProfileHead from "../components/SProfileHeader";
import { useTheme } from "@mui/material/styles";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import VideoCameraBackIcon from "@mui/icons-material/VideoCameraBack";

const parseAssessmentMessage = (message) => {
  if (!message) {
    return { steps: [], ambulanceText: "", footerLines: [] };
  }

  const lines = message
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);

  const steps = [];
  const footerLines = [];
  let ambulanceText = "";

  lines.forEach((line) => {
    const lower = line.toLowerCase();
    const isAmbulanceLine =
      lower.includes("recommended response") ||
      lower.includes("recommended ambulance") ||
      lower.includes("type of ambulance") ||
      line.includes("🚑");

    const isFooterLine =
      line.startsWith("💛") ||
      lower.includes("every small action") ||
      lower.includes("qrify is with you");

    if (isAmbulanceLine) {
      ambulanceText = line
        .replace(/^🚑\s*/u, "")
        .replace(/^recommended response:\s*/i, "")
        .replace(/^recommended ambulance:\s*/i, "")
        .replace(/^type of ambulance recommended:?\s*/i, "")
        .trim();
      return;
    }

    if (isFooterLine) {
      footerLines.push(line.replace(/^💛\s*/u, "").trim());
      return;
    }

    steps.push(line.replace(/^🩹\s*/u, "").trim());
  });

  return { steps, ambulanceText, footerLines };
};

const getUserImageUrl = (user) => {
  const imageUrl = user?.image?.image_data;
  return imageUrl ? `${imageUrl}?ts=${Date.now()}` : "";
};

const Emergencycall = () => {
  const [users, setUsers] = useState([]); // FamilyPin members
  const [selectedUser, setSelectedUser] = useState(null);
  const [userName, setUserName] = useState("");
  const [userImg, setUserImg] = useState("");
  const [loading, setLoading] = useState(false);
  const [thankYouMessage, setThankYouMessage] = useState("");
  const [callDone, setCallDone] = useState(false);
  const [ref_id, setRefId] = useState("");
  const [postscanId, setPostscanId] = useState("");
  const navigate = useNavigate();
  const { id } = useParams();
  const { txnId } = useSelector((state) => state.user);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const [openAvatar, setOpenAvatar] = useState(false);
  // 🔹 Injury AI states
  const [helperMessage, setHelperMessage] = useState("");
  const [openHelperDialog, setOpenHelperDialog] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [openUploadMenu, setOpenUploadMenu] = useState(false);
  const handleAvatarClick = () => setOpenAvatar(true);
  const handleCloseAvatar = () => setOpenAvatar(false);

  const applySelectedUser = (user) => {
    if (!user) return;
    const userId = user._id || "";
    setSelectedUser(user);
    setUserName(user.first_name || "");
    setRefId(userId);
    setPostscanId(userId);
    setUserImg(getUserImageUrl(user));
  };

  /* ---------------- FETCH USER ---------------- */
  useEffect(() => {
    if (!id) {
      console.warn("Emergency call: no pin/id in URL (/call/:id required)");
      return;
    }

    const fetchUserData = async () => {
      try {
        const res = await axios.get(ENDPOINT.GetDetails(id));
        console.log("User Response:", res.data);

        // Backend returns an object for Pin, or an array for FamilyPin
        if (Array.isArray(res.data)) {
          const familyUsers = res.data.filter(Boolean);
          setUsers(familyUsers);
          if (familyUsers.length > 0) {
            applySelectedUser(familyUsers[0]);
          }
        } else if (res.data) {
          setUsers([]);
          applySelectedUser(res.data);
        } else {
          console.error("getdetails returned empty payload for pin:", id);
        }
      } catch (err) {
        console.error("getdetails failed:", err?.response?.status, err?.response?.data || err);
      }
    };

    fetchUserData();
  }, [id]);

  /* ---------------- CLEAR CHAT ON PAGE LEAVE (FRONTEND ONLY) ---------------- */
  useEffect(() => {
    return () => {
      // ✅ Clear AI chat when user leaves this page
      sessionStorage.removeItem("ai_chat_messages");
      sessionStorage.removeItem("ai_chat_session");
      sessionStorage.removeItem("ai_chat_history");
    };
  }, []);

  // Injury Upload Handler
  // ===============================
  const handleInjurySelect = async (e) => {
    const file = e.target.files[0];
    // Reset so the same file can be selected again
    e.target.value = "";

    if (!file) return;

    if (!ref_id) {
      alert("User details not loaded yet. Please wait a moment and try again.");
      return;
    }

    // Backend analyze-injury currently works reliably with images
    if (!file.type.startsWith("image/")) {
      alert("Please upload an image (JPG/PNG). Video analysis is not supported yet.");
      setOpenUploadMenu(false);
      return;
    }

    // Guard against very large uploads that often crash the API
    const maxBytes = 8 * 1024 * 1024; // 8 MB
    if (file.size > maxBytes) {
      alert("Image is too large. Please upload a photo under 8 MB.");
      setOpenUploadMenu(false);
      return;
    }

    setOpenUploadMenu(false);
    setAnalyzing(true);

    try {
      const formData = new FormData();
      formData.append("ref_id", ref_id);
      formData.append("file", file);

      const res = await axios.post(ENDPOINT.AnalyzeInjury, formData, {
        timeout: 60000,
      });

      console.log("Injury analysis response:", res.data);

      const message =
        res.data?.message ||
        "Assessment completed. Please review the guidance below.";

      setHelperMessage(message);
      setOpenHelperDialog(true);
    } catch (error) {
      console.error("Injury analysis failed:", error);

      const status = error?.response?.status;
      const detail =
        error?.response?.data?.detail ||
        error?.response?.data?.message ||
        error?.message;

      let userMsg =
        "Injury analysis failed. Please try again with a clearer photo.";

      if (status === 500) {
        userMsg =
          "Server error while analysing the image (500). Please try another photo, or check the backend /analyse-injury service.";
      } else if (status === 422) {
        userMsg = "Invalid upload. Please select a valid image and try again.";
      } else if (error?.code === "ECONNABORTED") {
        userMsg = "Analysis timed out. Please try a smaller image.";
      } else if (!error?.response) {
        userMsg =
          "Cannot reach the analysis server. Check that the API at 192.168.0.68:8000 is running.";
      }

      if (typeof detail === "string" && detail.length < 180) {
        console.error("API detail:", detail);
      }

      alert(userMsg);
    } finally {
      setAnalyzing(false);
    }
  };

  /* ---------------- EMERGENCY CALL ---------------- */
  const handleCallClick = async () => {
    if (!selectedUser && !ref_id) return;

    setLoading(true);
    try {
      await axios.get(
        ENDPOINT.Emergency_Call(ref_id, userName, txnId),
        {
          headers: {
            "X-Action": "emergency_call",
            "X-User": userName,
          },
        }
      );

      setCallDone(true);
      setThankYouMessage("Thank you for reaching out. Help is on the way!");
    } catch (error) {
      console.error("Error making emergency call:", error);
      setThankYouMessage("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const renderAssessmentMessage = (message) => {
    const { steps, ambulanceText, footerLines } =
      parseAssessmentMessage(message);

    if (!steps.length && !ambulanceText && !footerLines.length) {
      return null;
    }

    return (
      <Stack spacing={2}>
        {steps.length > 0 && (
          <Box
            sx={{
              p: 2,
              borderRadius: 2,
              bgcolor: "#fff",
              border: "1px solid #E8EEF2",
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center" mb={1.25}>
              <HealthAndSafetyOutlinedIcon sx={{ color: "#1a237e", fontSize: 22 }} />
              <Typography fontWeight={700} sx={{ color: "#1a237e", fontSize: 15 }}>
                What To Do Now
              </Typography>
            </Stack>
            <Stack spacing={1.25}>
              {steps.map((step, index) => {
                const cleanStep = step
                  .replace(/^•\s*/, "")
                  .replace(/^[•●▪▫‣⁃]\s*/u, "")
                  .replace(/^\d+[\.\)\-:\s]+/, "")
                  .trim();

                if (!cleanStep) return null;

                return (
                  <Typography
                    key={index}
                    variant="body2"
                    sx={{
                      color: "#2c3e50",
                      lineHeight: 1.55,
                      textAlign: "left",
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 1,
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        color: "#1a237e",
                        fontWeight: 700,
                        fontSize: 18,
                        lineHeight: 1.2,
                        flexShrink: 0,
                      }}
                    >
                      •
                    </Box>
                    <Box component="span" sx={{ flex: 1 }}>
                      {cleanStep}
                    </Box>
                  </Typography>
                );
              })}
            </Stack>
          </Box>
        )}

        {ambulanceText && (
          <Box
            sx={{
              p: 2,
              borderRadius: 2,
              background: "linear-gradient(135deg, #FFF5F5 0%, #FFECEC 100%)",
              border: "1px solid #F5C6C6",
            }}
          >
            <Stack direction="row" spacing={1.25} alignItems="flex-start">
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 2,
                  bgcolor: "#fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  border: "1px solid #F5C6C6",
                }}
              >
                <LocalShippingOutlinedIcon sx={{ color: "#C0392B" }} />
              </Box>
              <Box sx={{ textAlign: "left", flex: 1 }}>
                <Typography
                  sx={{
                    fontWeight: 800,
                    fontSize: 14,
                    color: "#A61414",
                    mb: 0.5,
                    letterSpacing: 0.2,
                  }}
                >
                  Type of ambulance Recommended
                </Typography>
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: 15,
                    color: "#1a237e",
                    lineHeight: 1.4,
                  }}
                >
                  {ambulanceText}
                </Typography>
              </Box>
            </Stack>
          </Box>
        )}

        {footerLines.length > 0 && (
          <Box
            sx={{
              p: 2,
              borderRadius: 2,
              bgcolor: "#FFF8E7",
              border: "1px solid #F0D78C",
              textAlign: "left",
            }}
          >
            <Stack direction="row" spacing={1} alignItems="flex-start">
              <FavoriteBorderIcon sx={{ color: "#C9A227", fontSize: 20, mt: 0.2 }} />
              <Box>
                {footerLines.map((line, index) => (
                  <Typography
                    key={index}
                    sx={{
                      fontSize: index === 0 ? 14 : 13,
                      fontWeight: index === 0 ? 700 : 400,
                      color: index === 0 ? "#6B5A2A" : "#7A6A3A",
                      lineHeight: 1.45,
                      mb: index === footerLines.length - 1 ? 0 : 0.5,
                    }}
                  >
                    {line}
                  </Typography>
                ))}
              </Box>
            </Stack>
          </Box>
        )}
      </Stack>
    );
  };



  return (
    <Box
      sx={{
        // Use svh so mobile browser chrome doesn't clip bottom text
        height: { xs: "100svh", sm: "100dvh" },
        maxHeight: { xs: "100svh", sm: "100dvh" },
        width: "100%",
        display: "flex",
        justifyContent: "center",
        alignItems: isMobile ? "stretch" : "center",
        background: "linear-gradient(160deg, #EEF0FA 0%, #E8ECF8 40%, #DDE3F4 100%)",
        overflow: "hidden",
        position: isMobile ? "fixed" : "relative",
        inset: isMobile ? 0 : "auto",
      }}
    >
      <Stack
        alignItems="center"
        sx={{
          width: isMobile ? "100%" : isTablet ? 400 : 360,
          height: "100%",
          maxHeight: "100%",
          px: { xs: 2, sm: 2.5 },
          pt: { xs: 1, sm: 2 },
          // Clear Android/iOS browser bottom bars on all phones
          pb: {
            xs: "calc(56px + env(safe-area-inset-bottom, 0px))",
            sm: 2,
          },
          backgroundColor: "#fff",
          boxShadow: isMobile ? "none" : "0 12px 32px rgba(26, 35, 126, 0.12)",
          borderRadius: isMobile ? 0 : 3,
          position: "relative",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          boxSizing: "border-box",
        }}
      >
        <Box sx={{ width: "100%", position: "relative", flexShrink: 0, mb: 0.5 }}>
          <LogoBetweenLines
            logoSource={require("../assets/images/logo.png")}
            height={isMobile ? 52 : 60}
          />

          <Box
            onClick={() => {
              if (!postscanId) {
                alert("User details not loaded yet. Please wait a moment and try again.");
                return;
              }
              navigate(`/aichatscreen/${postscanId}`);
            }}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                if (!postscanId) return;
                navigate(`/aichatscreen/${postscanId}`);
              }
            }}
            sx={{
              position: "absolute",
              top: 2,
              right: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 0.4,
              cursor: postscanId ? "pointer" : "default",
              opacity: postscanId ? 1 : 0.7,
              zIndex: 2,
            }}
          >
            <Avatar
              src={require("../assets/images/swastika.png")}
              alt="Swastika"
              sx={{
                width: isMobile ? 48 : 52,
                height: isMobile ? 48 : 52,
                bgcolor: "#EEF0FA",
                border: "2.5px solid #1a237e",
                boxShadow: "0 3px 10px rgba(26, 35, 126, 0.22)",
                "& img": {
                  objectFit: "cover",
                  objectPosition: "top center",
                },
              }}
            />
            <Typography
              sx={{
                fontSize: 10,
                fontWeight: 700,
                color: "#1a237e",
                lineHeight: 1.1,
                textAlign: "center",
                maxWidth: 64,
              }}
            >
              Ask Swastika
            </Typography>
          </Box>
        </Box>

        <SProfileHead
          label1="Emergency Assistance !"
          size1={isMobile ? 18 : 22}
          color="#A61414"
          lineHeight1={isMobile ? 24 : 32}
          lineHeight2={isMobile ? 24 : 32}
        />

        {/* Family members — only when FamilyPin returns multiple users */}
        {users.length > 1 && (
          <Stack
            direction="row"
            spacing={1.5}
            justifyContent="center"
            flexWrap="wrap"
            useFlexGap
            mt={isMobile ? 1.5 : 2}
            width="100%"
            flexShrink={0}
          >
            {users.map((user) => {
              const isSelected = selectedUser?._id === user._id;
              return (
                <Stack
                  key={user._id}
                  alignItems="center"
                  spacing={0.4}
                  onClick={() => applySelectedUser(user)}
                  sx={{ cursor: "pointer", minWidth: 56 }}
                >
                  <Avatar
                    alt={user.first_name}
                    src={
                      user?.image?.image_data || "/static/images/avatar/1.jpg"
                    }
                    sx={{
                      width: isMobile ? 52 : 56,
                      height: isMobile ? 52 : 56,
                      border: isSelected
                        ? "2.5px solid #1a237e"
                        : "2px solid #EEF0FA",
                      boxShadow: isSelected
                        ? "0 3px 10px rgba(26, 35, 126, 0.25)"
                        : "none",
                      transition: "transform 0.15s ease",
                      "&:hover": { transform: "scale(1.08)" },
                    }}
                  />
                  <Typography
                    sx={{
                      fontSize: 10,
                      fontWeight: isSelected ? 800 : 600,
                      color: isSelected ? "#1a237e" : "#5C5FBD",
                      maxWidth: 64,
                      textAlign: "center",
                      lineHeight: 1.15,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {user.first_name}
                  </Typography>
                </Stack>
              );
            })}
          </Stack>
        )}

        <Stack
          direction="row"
          alignItems="center"
          justifyContent="center"
          mt={isMobile ? 2 : 3}
          spacing={isMobile ? 2.5 : 4}
          width="100%"
          flexShrink={0}
        >
          <Avatar
            src={userImg || "/static/images/avatar/1.jpg"}
            sx={{
              width: isMobile ? 78 : 88,
              height: isMobile ? 78 : 88,
              cursor: "pointer",
              border: "3px solid #EEF0FA",
              boxShadow: "0 4px 14px rgba(26, 35, 126, 0.15)",
            }}
            onClick={handleAvatarClick}
          />

          <Dialog open={openAvatar} onClose={handleCloseAvatar}>
            <IconButton
              onClick={handleCloseAvatar}
              sx={{ position: "absolute", top: 4, right: 4 }}
            >
              <CloseIcon />
            </IconButton>
            <img
              src={userImg || "/static/images/avatar/1.jpg"}
              alt="Profile"
              style={{ width: "100%" }}
            />
          </Dialog>

          <Stack spacing={0.35} alignItems="flex-start">
            <Typography
              sx={{
                color: "#5C5FBD",
                fontWeight: 700,
                fontSize: isMobile ? 13 : 15,
              }}
            >
              Please Help !
            </Typography>

            <Typography
              fontWeight={800}
              sx={{
                lineHeight: 1.15,
                fontSize: isMobile ? 22 : 26,
                color: "#1a237e",
              }}
            >
              {userName || (id ? "Loading..." : "Unknown user")}
            </Typography>
          </Stack>
        </Stack>

        {/* Injury Analysis — no card wrapper */}
        <Box
          sx={{
            mt: isMobile ? 2.5 : 3.5,
            width: "100%",
            flexShrink: 0,
            textAlign: "center",
          }}
        >
          <input
            type="file"
            accept="image/*,video/*"
            hidden
            id="upload-media"
            onChange={handleInjurySelect}
          />
          <input
            type="file"
            accept="image/*"
            capture="environment"
            hidden
            id="camera-image"
            onChange={handleInjurySelect}
          />
          <input
            type="file"
            accept="video/*"
            capture
            hidden
            id="camera-video"
            onChange={handleInjurySelect}
          />

          {analyzing ? (
            <Stack spacing={1} alignItems="center" py={2}>
              <CircularProgress size={30} sx={{ color: "#1a237e" }} />
              <Typography variant="caption" fontWeight={700} color="#1a237e">
                Analysing injury…
              </Typography>
            </Stack>
          ) : (
            <Box
              onClick={() => setOpenUploadMenu(true)}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                cursor: "pointer",
                px: 1,
                py: 1,
                transition: "transform 0.15s ease",
                "&:active": { transform: "scale(0.98)" },
              }}
            >
              <Box
                sx={{
                  width: 72,
                  height: 72,
                  borderRadius: "50%",
                  bgcolor: "#EEF0FA",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mb: 1,
                  border: "1.5px solid rgba(26, 35, 126, 0.12)",
                }}
              >
                <AddAPhotoIcon sx={{ fontSize: 34, color: "#1a237e" }} />
              </Box>
              <Typography
                fontWeight={800}
                fontSize={isMobile ? 16 : 18}
                color="#1a237e"
              >
                Injury Analysis
              </Typography>
              <Typography
                sx={{
                  mt: 0.4,
                  fontSize: 13,
                  fontWeight: 600,
                  color: "#5C5FBD",
                }}
              >
                Tap to add an image or a video of the injury
              </Typography>
              <Typography
                align="center"
                sx={{
                  maxWidth: 240,
                  mt: 0.75,
                  fontSize: isMobile ? 12.5 : 13.5,
                  lineHeight: 1.4,
                  color: "#667085",
                }}
              >
                This helps identify the type of injury to call the appropriate
                ambulance
              </Typography>
            </Box>
          )}
        </Box>

        <Dialog
          open={openUploadMenu}
          onClose={() => setOpenUploadMenu(false)}
          fullWidth
          PaperProps={{
            sx: {
              borderTopLeftRadius: 56,
              borderTopRightRadius: 56,
              position: "absolute",
              bottom: 10,
              m: 0,
            },
          }}
        >
          <Stack spacing={3} p={3}>

            <Typography fontWeight={700} textAlign="center">
              Add Injury Media
            </Typography>

            {/* ONE ROW – THREE OPTIONS */}
            <Stack direction="row" justifyContent="space-around">

              {/* Upload Image / Video */}
              <Box component="label" htmlFor="upload-media" sx={menuItem}>
                <AddAPhotoIcon sx={menuIcon} />
                <Typography variant="caption" align="center">
                  Upload<br />Image / Video
                </Typography>
              </Box>

              {/* Take Photo */}
              <Box component="label" htmlFor="camera-image" sx={menuItem}>
                <CameraAltIcon sx={menuIcon} />
                <Typography variant="caption">Take Photo</Typography>
              </Box>

              {/* Record Video */}
              <Box component="label" htmlFor="camera-video" sx={menuItem}>
                <VideoCameraBackIcon sx={menuIcon} />
                <Typography variant="caption">Record Video</Typography>
              </Box>

            </Stack>

          </Stack>
        </Dialog>



        {/* ===============================
    HELPER MESSAGE DIALOG (MATCH CARD WIDTH)
=============================== */}
        <Dialog
          open={openHelperDialog}
          onClose={() => setOpenHelperDialog(false)}
          fullWidth
          maxWidth="sm"
          PaperProps={{
            sx: {
              width: isMobile ? "92%" : isTablet ? "40vh" : 360,
              maxWidth: "100%",
              borderRadius: 3,
              maxHeight: "80vh",
              display: "flex",
              flexDirection: "column",
              m: "auto",
              overflow: "hidden",
            },
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            px={2}
            pt={2}
            pb={1.5}
            sx={{
              background: "linear-gradient(135deg, #1a237e 0%, #343275 55%, #5C5FBD 100%)",
              color: "#fff",
            }}
          >
            <Box sx={{ textAlign: "left", pr: 1 }}>
              <Typography fontWeight={700} sx={{ fontSize: 16, lineHeight: 1.3 }}>
                Stay Calm – Here’s What To Do
              </Typography>
              <Typography sx={{ fontSize: 12, opacity: 0.85, mt: 0.35 }}>
                AI injury assessment result
              </Typography>
            </Box>

            <IconButton
              onClick={() => setOpenHelperDialog(false)}
              sx={{ color: "#fff", "&:hover": { bgcolor: "rgba(255,255,255,0.12)" } }}
            >
              <CloseIcon />
            </IconButton>
          </Stack>

          <Box
            sx={{
              p: 2,
              overflowY: "auto",
              flex: 1,
              backgroundColor: "#F4F7F8",
            }}
          >
            {renderAssessmentMessage(helperMessage)}
          </Box>
        </Dialog>

        {/* CALL AREA — raised slightly so footer stays above browser nav */}
        <Stack
          alignItems="center"
          sx={{
            mt: "auto",
            mb: { xs: 1, sm: 0 },
            width: "100%",
            flexShrink: 0,
            pt: { xs: 1, sm: 1.5 },
          }}
        >
          {loading ? (
            <CircularProgress size={32} sx={{ color: "#1a237e", my: 0.5 }} />
          ) : (
            <Fab
              disabled={callDone}
              aria-label="Call emergency"
              sx={{
                backgroundColor: "#22A45A",
                color: "#fff",
                width: { xs: 58, sm: 68 },
                height: { xs: 58, sm: 68 },
                boxShadow: "0 8px 22px rgba(34, 164, 90, 0.4)",
                animation: callDone ? "none" : "pulse 1.6s infinite",
                "@keyframes pulse": {
                  "0%": {
                    transform: "scale(1)",
                    boxShadow: "0 0 0 0 rgba(34,164,90,0.55)",
                  },
                  "70%": {
                    transform: "scale(1.08)",
                    boxShadow: "0 0 0 14px rgba(34,164,90,0)",
                  },
                  "100%": {
                    transform: "scale(1)",
                    boxShadow: "0 0 0 0 rgba(34,164,90,0)",
                  },
                },
              }}
              onClick={handleCallClick}
            >
              <CallIcon sx={{ fontSize: { xs: 26, sm: 28 } }} />
            </Fab>
          )}

          <Typography
            fontWeight={800}
            pt={0.75}
            fontSize={{ xs: 15, sm: 17 }}
            color="#1a237e"
            textAlign="center"
            lineHeight={1.2}
          >
            {thankYouMessage || "Call Emergency"}
          </Typography>

          {!thankYouMessage && (
            <Typography
              textAlign="center"
              sx={{
                mt: 0.25,
                fontSize: { xs: 12, sm: 12.5 },
                color: "#667085",
                lineHeight: 1.25,
                px: 1,
              }}
            >
              ( Please click the call button to Help {userName} )
            </Typography>
          )}

          <Typography
            textAlign="center"
            sx={{
              mt: 0.75,
              fontSize: { xs: 11.5, sm: 12 },
              fontWeight: 700,
              lineHeight: 1.3,
              color: "#A61414",
            }}
          >
            svasth.ai{" "}
            <Box component="span" sx={{ color: "#5C5FBD", fontWeight: 600 }}>
              · 24/7 Available
            </Box>
          </Typography>
        </Stack>
      </Stack>
    </Box>
  );
};

export default Emergencycall;

const menuItem = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  cursor: "pointer",
  gap: 0.5,
};

const menuIcon = {
  fontSize: 36,
  color: "#1a237e",
};
